import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { whiteBG, borderPrimary, primaryText, smallTextSize, tertiaryText } from '../../../styling/stylingHelper';

interface Props {
  restaurants: { id: string; name: string }[];
  selectedIds: string[];
  requiredIds?: string[];
  onChange: (ids: string[]) => void;
  label: string;
  disabled?: boolean;
}

/** Same compact restaurant selector for setup and the group results view. */
export default function SourcingRestaurantPicker({ restaurants, selectedIds, requiredIds = [], onChange, label, disabled }: Props) {
  const { t } = useTranslation();
  const effectiveIds = Array.from(new Set([...selectedIds, ...requiredIds]));
  return (
    <details className="relative max-w-full">
      <summary className={cn(whiteBG, borderPrimary, primaryText, smallTextSize, 'flex cursor-pointer list-none items-center gap-2 rounded-lg border px-3 py-2')}>
        <span>{label}: {effectiveIds.length === restaurants.length ? t('products.allRestaurants') : t('products.restaurantsSelected', { count: effectiveIds.length, total: restaurants.length })}</span>
        <ChevronDown className="h-4 w-4 shrink-0" />
      </summary>
      <div className={cn(whiteBG, borderPrimary, 'absolute left-0 z-20 mt-2 max-h-80 w-72 max-w-[calc(100vw-3rem)] overflow-y-auto rounded-xl border p-2 shadow-lg')}>
        <button type="button" disabled={disabled} onClick={() => onChange(restaurants.map(r => r.id))} className={cn(primaryText, smallTextSize, 'px-2 py-1.5 underline disabled:opacity-50')}>
          {t('products.selectAll')}
        </button>
        {restaurants.map(restaurant => (
          <label key={restaurant.id} className={cn(primaryText, smallTextSize, 'flex items-center gap-2 rounded-lg px-2 py-2 hover:bg-gray-50 dark:hover:bg-gray-800/50')}>
            <input type="checkbox" checked={effectiveIds.includes(restaurant.id)} disabled={disabled || requiredIds.includes(restaurant.id)} onChange={event => onChange(event.target.checked ? [...effectiveIds, restaurant.id] : effectiveIds.filter(id => id !== restaurant.id))} />
            <span className="min-w-0 truncate">{restaurant.name}</span>
            {requiredIds.includes(restaurant.id) && <span className={cn(tertiaryText, 'text-xs')}>{t('products.sourcing.required')}</span>}
          </label>
        ))}
      </div>
    </details>
  );
}
